import { NavLink } from 'react-router-dom';
import { Sr } from '../util/a11y.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { ToastHistoryButton } from './Toasts.jsx';
import { ActivityBellButton } from './ActivityPanel.jsx';
import { useNavCounts } from '../hooks/useNavCounts.js';
import { ROUTES, badgeLabel } from '../nav.js';

// T079 — Sidebar nav with live counts. The badge logic is per-item:
//   - Dashboard:    "X new" (new-status recs awaiting triage)
//   - Applications: "X in interview/screening/offer" (active pipeline)
//   - Profile, Documents, Settings: no badge — nothing to count meaningfully
// Counts come from `useNavCounts`, which polls /recommendations/stats every
// 60s and exposes a manual `refresh()` for action-after-update.
const NAV = ROUTES; // T271(2) — one list, in nav.js

// T121 — desktop rail. Hidden below md (the mobile shell renders SidebarContent
// inside a slide-over drawer instead — see MobileTopBar).
export default function Sidebar() {
  return (
    // The rail is a flex child of a min-h-screen row, so it was exactly as tall
    // as the page and scrolled away with it: on a long dashboard the nav, the
    // badges and sign-out were a full scroll back. sticky + h-screen pins it to
    // the viewport; its own overflow-y keeps a tall nav reachable on a short
    // screen rather than clipping it.
    <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col overflow-y-auto border-r border-slate-800 bg-slate-900/60 md:flex">
      <SidebarContent />
    </aside>
  );
}

// T121 — shared nav body, rendered by BOTH the desktop rail and the mobile
// drawer. `onNavigate` is set only by the drawer: it closes the drawer on tap
// and bumps hit targets to ≥44px for touch. Desktop stays compact.
export function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const counts = useNavCounts();
  const touch = onNavigate ? ' min-h-[44px]' : '';

  return (
    <>
      <div className="border-b border-slate-800 p-5">
        {/* T433 — every other row in this rail navigates and this one did
            not, which is the one place a wordmark is always expected to. */}
        <NavLink to="/" className="text-lg font-semibold tracking-tight text-brand-500 transition hover:text-brand-400">
          hunter
        </NavLink>
        <div className="mt-0.5 text-xs text-slate-500">Job Search OS</div>
      </div>

      <nav className="flex-1 space-y-1 p-3">
        {NAV.map((item) => {
          const count = item.badgeKey ? counts[item.badgeKey] : 0;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              className={({ isActive }) =>
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition' +
                touch +
                ' ' +
                (isActive
                  ? 'bg-brand-600/15 text-brand-500'
                  : 'text-slate-400 hover:bg-slate-800/60 hover:text-slate-200')
              }
            >
              {/* T250 — the glyph is decoration; the label beside it is the name.
                  Unhidden, a screen reader reads "◉ Dashboard" and the operator
                  hears a character with no meaning before every item. */}
              <span className="w-4 text-xs" aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
              {count > 0 && (
                <span
                  className="ml-auto rounded-full bg-brand-600/20 px-1.5 py-0.5 font-mono text-[10px] text-brand-400"
                  title={badgeLabel(item.to, count)}
                >
                  {count}
                  {/* The bare number is ambiguous read aloud — "Dashboard 12".
                      The suffix is the same sentence the tooltip already shows. */}
                  <Sr> {badgeLabel(item.to, count)}</Sr>
                </span>
              )}
            </NavLink>
          );
        })}
        {/* T132 — durable activity feed (scans, AI generations, source errors) */}
        <ActivityBellButton onNavigate={onNavigate} />
        {/* T077 — Notification history drawer */}
        <ToastHistoryButton />
        {/* T074 — hint that the palette exists; clicking dispatches Cmd+K. */}
        <button
          type="button"
          onClick={() => {
            window.dispatchEvent(new CustomEvent('hunter:open-palette'));
            onNavigate?.();
          }}
          className={
            'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-slate-400 transition hover:bg-slate-800/60 hover:text-slate-200' +
            touch
          }
        >
          <span className="w-4 text-xs">⌘</span>
          <span>Quick jump</span>
          <kbd className="ml-auto rounded border border-slate-700 bg-slate-950/40 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">⌘K</kbd>
        </button>
      </nav>

      {user && (
        <div className="border-t border-slate-800 p-4 text-xs">
          <div className="truncate text-slate-300">{user.email}</div>
          <div className="text-slate-500">role: {user.role}</div>
          <button
            type="button"
            onClick={logout}
            className="mt-2 text-slate-500 underline decoration-dotted hover:text-brand-500"
          >
            Sign out
          </button>
        </div>
      )}
    </>
  );
}
